import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { timeline } from "../data/site";

export const TimelineSection = ({ items = timeline, exploreLink = false, projectsLink = false }) => {
  const scrollRef = useRef(null);
  const scrollBy = (dir) => scrollRef.current?.scrollBy({ left: dir * 360, behavior: "smooth" });

  return (
    <section id="track-record" data-testid="track-record-section" className="bg-navy text-white py-24 sm:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12 sm:mb-16">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/50 mb-4">1972 — Today</p>
            <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-tight max-w-2xl">
              Landmark work, decade after decade
            </h2>
          </div>
          <div className="hidden sm:flex gap-3">
            <button data-testid="timeline-prev-button" onClick={() => scrollBy(-1)} aria-label="Scroll timeline back" className="w-11 h-11 border border-white/25 flex items-center justify-center transition-colors duration-300 hover:bg-white/10">
              <ChevronRight size={18} className="rotate-180" />
            </button>
            <button data-testid="timeline-next-button" onClick={() => scrollBy(1)} aria-label="Scroll timeline forward" className="w-11 h-11 border border-white/25 flex items-center justify-center transition-colors duration-300 hover:bg-white/10">
              <ChevronRight size={18} />
            </button>
          </div>
        </Reveal>
      </div>
      <Reveal>
        <div
          ref={scrollRef}
          data-testid="timeline-scroller"
          className="bleed-scroll flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory pl-5 sm:pl-8 pr-5 sm:pr-8 pb-4 cursor-grab active:cursor-grabbing"
        >
          {items.map((t) => (
            <article key={t.era} className="snap-start shrink-0 w-[82vw] sm:w-[400px] group">
              <div className="aspect-[16/10] overflow-hidden border border-white/15 bg-navy-deep">
                {t.img ? (
                  <img
                    src={t.img}
                    alt={t.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" aria-hidden="true">
                    <span className="font-serif text-6xl text-white/10 select-none">{t.era}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 mt-6">
                <span className="shrink-0 bg-white/5 border border-white/15 px-4 py-2 font-serif text-xl text-brand tracking-tight">
                  {t.era}
                </span>
                <span className="flex-1 h-px bg-gradient-to-r from-brand/60 to-white/10" aria-hidden="true" />
              </div>
              <h3 className="mt-5 font-serif text-xl sm:text-2xl tracking-tight">{t.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{t.text}</p>
            </article>
          ))}
        </div>
      </Reveal>
      <div className="mx-auto max-w-7xl px-5 sm:px-8 mt-10 grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
        <div className="justify-self-center sm:justify-self-start">
          {projectsLink && (
            <Link
              to="/projects"
              data-testid="timeline-projects-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white border-b-2 border-brand pb-1 transition-colors duration-300 hover:text-brand"
            >
              Explore active projects <ArrowRight size={16} />
            </Link>
          )}
        </div>
        <p className="justify-self-center text-xs uppercase tracking-[0.2em] text-white/40 text-center">Drag or scroll sideways to move through five decades</p>
        <div className="justify-self-center sm:justify-self-end">
          {exploreLink && (
            <Link
              to="/track-record"
              data-testid="timeline-explore-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white border-b-2 border-brand pb-1 transition-colors duration-300 hover:text-brand"
            >
              Explore the entire timeline <ArrowRight size={16} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
